import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Model {
  id: string
  name: string
  version: string
  framework: string
  status: string
  accuracy: number
  created: string
}

interface Framework {
  name: string
  models: number
  percentage: number
}

interface Statistics {
  total_models: number
  production_models: number
  staging_models: number
  archived_models: number
  model_versions: number
  avg_deployment_time_hrs: number
}

export default function ModelRegistryPage() {
  const [models, setModels] = useState<Model[]>([])
  const [frameworks, setFrameworks] = useState<Framework[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [modelsRes, frameworksRes, statsRes] = await Promise.all([
        fetch('/api/modelregistry/models'),
        fetch('/api/modelregistry/frameworks'),
        fetch('/api/modelregistry/statistics')
      ])
      if (modelsRes.ok) setModels(await modelsRes.json())
      if (frameworksRes.ok) setFrameworks(await frameworksRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching model registry:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    if (status === 'Production') return 'bg-green-100 text-green-800'
    if (status === 'Staging') return 'bg-yellow-100 text-yellow-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Model Registry</h1>
          <p className="mt-2 text-gray-600">Phase 271 - ML model version management</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Models</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_models}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Production</p>
              <p className="text-2xl font-bold text-green-600">{statistics.production_models}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Staging</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.staging_models}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Archived</p>
              <p className="text-2xl font-bold text-gray-600">{statistics.archived_models}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Versions</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.model_versions}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Deploy Time</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_deployment_time_hrs}h</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">ML Frameworks</h2>
          </div>
          <div className="p-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {frameworks.map((fw, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="font-medium text-gray-900">{fw.name}</p>
                <p className="text-2xl font-bold text-blue-600">{fw.models}</p>
                <p className="text-sm text-gray-500">{fw.percentage}%</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Registered Models</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Version</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Framework</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accuracy</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {models.map((model) => (
                  <tr key={model.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{model.name}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{model.version}</span>
                    </td>
                    <td className="px-6 py-4 text-purple-600">{model.framework}</td>
                    <td className="px-6 py-4 text-green-600 font-medium">{model.accuracy}%</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(model.status)}`}>{model.status}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{model.created}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
